import config from 'constants/config'
import { getImageUrl } from 'lib/image/imageUtils'

export type SocialVertical = 'hotels' | 'flights' | 'experiences' | 'tours' | 'cruises' | 'villas' | 'cars'

export type SocialImageCandidate = ({ id: string } | {
  url: string
  width?: number
  height?: number
  mimeType?: string
}) & { alt?: string }

export interface ResolvedSocialImage {
  url: string
  alt: string
  width?: number
  height?: number
  mimeType?: string
  source: 'page' | 'destination' | 'vertical' | 'brand'
}

// Existing public hero assets; other brands retain their own configured image.
const verticalImages: Record<SocialVertical, string> = {
  hotels: '8d2cc64monviv73whp4',
  flights: 'Flights_wl4url',
  experiences: 'nz83bzbyhxc8r1b8t8m',
  tours: 'adkvxdgnii1kvb4s970c',
  cruises: 'xwa3kj2zjknsv7nbgctk',
  villas: 'rentals-landing-page-canada',
  cars: 'fbuik31c2rvjvo7mtk2p',
}

export function getSocialImageUrl(id: string): string {
  return getImageUrl(id, {
    width: 1200,
    aspectRatio: '1200:630',
    gravity: 'center',
    format: 'jpeg',
    quality: 'eco',
  })
}

function resolveCandidate(candidate: SocialImageCandidate | undefined, alt: string): Omit<ResolvedSocialImage, 'source'> | undefined {
  if (!candidate) return undefined
  if ('id' in candidate) {
    const id = candidate.id.trim()
    if (!id) return undefined
    return { url: getSocialImageUrl(id), alt: candidate.alt || alt, width: 1200, height: 630, mimeType: 'image/jpeg' }
  }
  try {
    const url = new URL(candidate.url)
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return undefined
    return {
      url: url.href,
      alt: candidate.alt || alt,
      width: candidate.width,
      height: candidate.height,
      mimeType: candidate.mimeType,
    }
  } catch {
    return undefined
  }
}

export function resolveSocialImage({ image, destination, vertical, alt, restricted = false }: {
  image?: SocialImageCandidate
  destination?: SocialImageCandidate
  vertical?: SocialVertical
  alt: string
  restricted?: boolean
}): ResolvedSocialImage | undefined {
  const candidates: Array<[ResolvedSocialImage['source'], SocialImageCandidate | undefined]> = [
    ['page', image],
    ['destination', restricted ? undefined : destination],
    ['vertical', !restricted && vertical && config.BRAND === 'luxuryescapes' ? { id: verticalImages[vertical] } : undefined],
    ['brand', { id: config.head.brandOpenGraphImageId }],
  ]
  for (const [source, candidate] of candidates) {
    const resolved = resolveCandidate(candidate, alt)
    if (resolved) return { ...resolved, source }
  }
  return undefined
}
